/**
 * Tutorial video taxonomy — must match `TutorialVideoCategory` and
 * `TutorialVideoSource` in
 * `package/src/tutorial-video/tutorial-video.constants.ts`, which the api
 * validates `category` against.
 *
 * Declared here rather than imported: `@therapylake/package`'s only
 * browser-built entry point is `sentry-shared` (see
 * `package/tsconfig.frontend.json`), so importing `@therapylake/package/types`
 * does not survive `npm run build`. These are string enums, so a mismatch
 * surfaces immediately as a rejected request.
 */
export const TutorialVideoCategory = {
    GENERAL: 'GENERAL',
    TRAINING: 'TRAINING',
    HOW_TO: 'HOW_TO',
    ONBOARDING: 'ONBOARDING',
};

/** Where a video came from. Computed by the api at read time, never stored. */
export const TutorialVideoSource = {
    /** Authored by TherapyLake. Read-only for the org. */
    PLATFORM: 'PLATFORM',
    /** Authored by this org. Editable with TUTORIAL_VIDEO_MANAGE. */
    ORG: 'ORG',
};

/** Human-readable labels for the sections above. */
export const TutorialVideoCategoryLabels = {
    GENERAL: 'General',
    TRAINING: 'Training',
    HOW_TO: 'How-to',
    ONBOARDING: 'Onboarding',
};

/** Tab order in the Help Center, and the order of the category picker. */
export const TUTORIAL_VIDEO_CATEGORY_ORDER = [
    TutorialVideoCategory.TRAINING,
    TutorialVideoCategory.HOW_TO,
    TutorialVideoCategory.ONBOARDING,
    TutorialVideoCategory.GENERAL,
];

export const ACTION_TYPE = 'GET_TUTORIAL_VIDEOS';
export const CREATE_ACTION_TYPE = 'CREATE_TUTORIAL_VIDEO';
export const EDIT_ACTION_TYPE = 'EDIT_TUTORIAL_VIDEO';
export const DELETE_ACTION_TYPE = 'DELETE_TUTORIAL_VIDEO';

/** Pseudo-category for the leading "All Videos" tab. */
export const ALL_VIDEOS_TAB = 'ALL';

/** Dropdown options for the category picker in the add/edit form. */
export const CATEGORY_OPTIONS = TUTORIAL_VIDEO_CATEGORY_ORDER.map((category) => ({
    id: category,
    name: TutorialVideoCategoryLabels[category],
}));

/**
 * The tab strip: "All Videos" then every category, always shown.
 *
 * Fixed rather than derived from the data, because an org can now author its
 * own videos and pick any of the four sections — a tab that appears and
 * disappears as the last video in it is deleted would make the page feel
 * unstable. Empty sections render an empty state instead.
 */
export const buildTabs = (videos = []) => {
    const counts = (videos || []).reduce((acc, video) => {
        acc[video.category] = (acc[video.category] || 0) + 1;
        return acc;
    }, {});

    return [
        { value: ALL_VIDEOS_TAB, label: 'All Videos', count: videos?.length || 0 },
        ...TUTORIAL_VIDEO_CATEGORY_ORDER.map((category) => ({
            value: category,
            label: TutorialVideoCategoryLabels[category] || category,
            count: counts[category] || 0,
        })),
    ];
};

/** Seconds → `m:ss` / `h:mm:ss`, the way a player shows a runtime. */
export const formatDuration = (durationSec) => {
    if (!durationSec || durationSec < 0) return '';
    const total = Math.round(durationSec);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    const paddedSeconds = String(seconds).padStart(2, '0');
    return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}` : `${minutes}:${paddedSeconds}`;
};

/**
 * Read a video file's runtime in the browser before upload.
 *
 * The api stores `durationSec` but has no way to derive it — bytes go
 * straight from the browser to S3, so nothing server-side ever opens the
 * file. Resolves `null` rather than rejecting: a missing duration costs a
 * badge on the card, and is not worth failing an upload over.
 */
export const readVideoDuration = (file) =>
    new Promise((resolve) => {
        try {
            const url = URL.createObjectURL(file);
            const element = document.createElement('video');
            element.preload = 'metadata';
            element.onloadedmetadata = () => {
                URL.revokeObjectURL(url);
                resolve(Number.isFinite(element.duration) ? Math.round(element.duration) : null);
            };
            element.onerror = () => {
                URL.revokeObjectURL(url);
                resolve(null);
            };
            element.src = url;
        } catch {
            resolve(null);
        }
    });
